/**
 * ScrollableTabs — headless tab BAR (no panel) with WAI-ARIA APG keyboard nav
 *                  and intra-tablist horizontal scroll.
 *
 * Division: UI (Layer 8) - Dashboard Shared Library
 * Pattern: ARIA tablist/tab, controlled activeId, roving tabindex,
 *          ArrowLeft/ArrowRight (cycle) + Home/End keyboard nav,
 *          scroll-into-view on activeId change.
 *
 * Why this exists alongside <Tabs>:
 * - <Tabs> is content-coupled (takes Tab[] with `content: ReactNode` and
 *   renders the active panel inline). Good for inline-content cases like
 *   ClaudeSessions.tsx.
 * - <ScrollableTabs> renders ONLY the bar — caller decides where/how to
 *   render the active panel (e.g. via React Router outlet, Suspense+lazy,
 *   parent state machine). This matches the bespoke tablists in 15+ files
 *   across crm/portal/ops/seo/booking/email apps.
 *
 * Use the `getTabClassName` / `getBadgeClassName` escape hatches when a
 * consumer needs per-tab custom colors (e.g. PipelineTabBar's per-key
 * brand colors). Default rendering covers the standard underline + pill
 * variants documented in the design system.
 *
 * Width / scroll behavior: `overflow-x-auto` ensures wide tab bars (>6
 * tabs at mobile widths) scroll within the tablist instead of overflowing
 * the flex parent. Without this, the browser auto-scrolls the parent
 * <main> when a focused tab needs to be brought into view, which can
 * shift page content behind a fixed/flex sidebar at narrow widths. See
 * the 2026-04-27 mobile-sidebar overlap incident (commit e4d9018166).
 *
 * @layer widget
 * @package @plerity/dashboard-shared
 */

import { cn } from "@plerity/ui-core";
import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useId, useRef } from "react";

/**
 * A single tab descriptor for the tablist.
 *
 * `id` is used as both the React key and the synthetic ARIA id stem
 * (sanitized via `toIdSegment`). `label` is the visible content rendered
 * inside the button — a string for typical text tabs, or `ReactNode` when
 * the tab needs an inline icon next to the text (`<Icon /> Label`). When
 * `label` is a non-string ReactNode, callers SHOULD provide
 * `accessibilityLabel` so screen readers receive a clean text announcement
 * even if the rendered ReactNode contains decorative SVGs. `badge`
 * (if provided) renders inline after the label as a small pill — typical
 * uses are entity counts ("Active 12") or status indicators.
 */
export interface ScrollableTabItem {
  /** Stable identifier — used for React key + ARIA id; passed back to onChange. */
  id: string;
  /**
   * Visible content rendered inside the button. String is the common case;
   * pass a ReactNode (e.g. `<><Icon /> Label</>`) when the tab needs an
   * inline icon. For ReactNode labels, set `accessibilityLabel` so the
   * accessible name remains a clean text string.
   */
  label: ReactNode;
  /** Optional badge (count or status pill) rendered after the label. */
  badge?: ReactNode;
  /** When true, the tab is rendered but not focusable / clickable. */
  disabled?: boolean;
  /** Optional override for the button's accessible name (defaults to `label`). */
  accessibilityLabel?: string;
}

/** Visual variant for the tab bar. */
export type ScrollableTabsVariant = "underline" | "pill";

export interface ScrollableTabsProps {
  /** The tab items to render, in display order. */
  tabs: ScrollableTabItem[];
  /** Currently-active tab id. Must match one of `tabs[].id`. */
  activeId: string;
  /** Called when the user activates a different tab (click or keyboard). */
  onChange: (id: string) => void;
  /** Visual variant. Default: 'underline'. */
  variant?: ScrollableTabsVariant;
  /** Required accessible label for the tablist (e.g. "Pipeline tabs"). */
  ariaLabel: string;
  /** Optional className applied to the outer tablist <div>. */
  className?: string;
  /**
   * Escape hatch for per-tab custom button classNames. When provided,
   * REPLACES the default variant classes. Use sparingly — prefer the
   * built-in variants for consistency.
   */
  getTabClassName?: (tab: ScrollableTabItem, isActive: boolean) => string;
  /**
   * Escape hatch for per-tab badge classNames. When provided, REPLACES
   * the default badge classes. Only meaningful when `badge` is present.
   */
  getBadgeClassName?: (tab: ScrollableTabItem, isActive: boolean) => string;
  /**
   * Deterministic stem for the generated `tab-*` / `tabpanel-*` element ids.
   *
   * By default the stem comes from React's `useId()`, which produces an
   * opaque value (e.g. `:r7:`) that a consumer CANNOT predict. That is fine
   * for a bare tab bar, but it silently breaks the ARIA tab↔panel
   * relationship whenever the consumer renders its own panels: each tab
   * emits `aria-controls="tabpanel-<useId>-<id>"` pointing at an element id
   * that does not exist, so assistive tech has no path from a tab to the
   * content it controls (WCAG 1.3.1 / 4.1.2).
   *
   * Pass a stable prefix (e.g. `idPrefix="assignments"`) and render each
   * panel with the matching pair:
   *
   *   <div id={`tabpanel-assignments-${tabId}`}
   *        role="tabpanel"
   *        aria-labelledby={`tab-assignments-${tabId}`} />
   *
   * Omitting it preserves the previous `useId()` behavior exactly.
   */
  idPrefix?: string;
}

/**
 * Build the tab / tabpanel element ids for a given stem + tab id.
 * Exported so consumers rendering their own panels stay in lockstep with
 * the ids this component emits in `aria-controls` — no string duplication.
 */
export function scrollableTabIds(stem: string, tabId: string): { tabId: string; panelId: string } {
  const seg = toIdSegment(tabId);
  return { tabId: `tab-${stem}-${seg}`, panelId: `tabpanel-${stem}-${seg}` };
}

/** Sanitize an id for safe use in HTML id attributes. */
function toIdSegment(v: string): string {
  return v.replace(/[^a-zA-Z0-9_-]/g, "-");
}

/** Default per-variant button classes. */
function defaultTabClassName(variant: ScrollableTabsVariant, isActive: boolean): string {
  const base = cn(
    "whitespace-nowrap text-sm font-medium transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
  );

  if (variant === "pill") {
    return cn(
      base,
      "rounded-md px-3 py-1.5",
      isActive
        ? "bg-white text-brand-700 shadow-sm dark:bg-gray-700 dark:text-brand-300"
        : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
    );
  }

  // underline (default)
  return cn(
    base,
    "border-b-2 -mb-px px-4 py-2",
    isActive
      ? "border-brand-500 text-brand-700 dark:border-brand-400 dark:text-brand-300"
      : "border-transparent text-muted hover:text-gray-700 dark:hover:text-gray-300"
  );
}

/** Default per-variant badge classes. */
function defaultBadgeClassName(variant: ScrollableTabsVariant, isActive: boolean): string {
  const base = "ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
  if (isActive) {
    return cn(
      base,
      variant === "pill"
        ? "bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
        : "bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
    );
  }
  return cn(base, "bg-surface-subtle text-gray-700 dark:text-gray-300");
}

/** Default per-variant tablist container classes. */
function defaultTablistClassName(variant: ScrollableTabsVariant): string {
  if (variant === "pill") {
    return "flex items-center gap-1 overflow-x-auto rounded-lg bg-gray-100 p-1 dark:bg-gray-800";
  }
  return "flex items-center overflow-x-auto border-b border-light";
}

export function ScrollableTabs({
  tabs,
  activeId,
  onChange,
  variant = "underline",
  ariaLabel,
  className,
  getTabClassName,
  getBadgeClassName,
  idPrefix,
}: ScrollableTabsProps) {
  // `useId()` must be called unconditionally (rules of hooks); `idPrefix`
  // only overrides the resulting value.
  const autoId = useId();
  const tabsId = idPrefix ?? autoId;
  const buttonRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());
  const tablistRef = useRef<HTMLDivElement | null>(null);

  // Filter to the navigable (non-disabled) subset for keyboard cycling.
  const enabledTabs = tabs.filter((t) => !t.disabled);

  const focusAndActivate = useCallback(
    (id: string) => {
      onChange(id);
      // Defer focus to next tick so the activation state lands first
      // (consumers re-render with the new activeId, then we move focus).
      requestAnimationFrame(() => {
        buttonRefs.current.get(id)?.focus();
      });
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (enabledTabs.length === 0) return;

      // Find current index in the ENABLED subset (skip past any disabled
      // active tab — defensive; usually activeId is enabled).
      const currentIdx = Math.max(
        0,
        enabledTabs.findIndex((t) => t.id === activeId)
      );

      let nextId: string | null = null;
      if (e.key === "ArrowRight") {
        const ni = (currentIdx + 1) % enabledTabs.length;
        nextId = enabledTabs[ni].id;
      } else if (e.key === "ArrowLeft") {
        const ni = (currentIdx - 1 + enabledTabs.length) % enabledTabs.length;
        nextId = enabledTabs[ni].id;
      } else if (e.key === "Home") {
        nextId = enabledTabs[0].id;
      } else if (e.key === "End") {
        nextId = enabledTabs[enabledTabs.length - 1].id;
      }

      if (nextId !== null) {
        e.preventDefault();
        focusAndActivate(nextId);
      }
    },
    [activeId, enabledTabs, focusAndActivate]
  );

  // Scroll the active tab into view when activeId changes (e.g. via
  // external state, deep-link, or keyboard navigation past the visible
  // viewport edge).
  useEffect(() => {
    const btn = buttonRefs.current.get(activeId);
    if (!btn || !tablistRef.current) return;
    // Skip on first render if the button is already in view — avoid the
    // initial layout shift of an unconditional scrollIntoView call.
    const list = tablistRef.current;
    const btnLeft = btn.offsetLeft;
    const btnRight = btnLeft + btn.offsetWidth;
    const listLeft = list.scrollLeft;
    const listRight = listLeft + list.clientWidth;
    if (btnLeft < listLeft || btnRight > listRight) {
      btn.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    }
  }, [activeId]);

  return (
    <div
      ref={tablistRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={cn(defaultTablistClassName(variant), className)}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        const seg = toIdSegment(tab.id);
        const tabClass = getTabClassName ? getTabClassName(tab, isActive) : defaultTabClassName(variant, isActive);
        const badgeClass = getBadgeClassName
          ? getBadgeClassName(tab, isActive)
          : defaultBadgeClassName(variant, isActive);

        return (
          <button
            key={tab.id}
            ref={(el) => {
              if (el) buttonRefs.current.set(tab.id, el);
              else buttonRefs.current.delete(tab.id);
            }}
            type="button"
            role="tab"
            id={`tab-${tabsId}-${seg}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${tabsId}-${seg}`}
            aria-label={tab.accessibilityLabel}
            tabIndex={isActive ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => !tab.disabled && onChange(tab.id)}
            className={tabClass}
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge !== null && <span className={badgeClass}>{tab.badge}</span>}
          </button>
        );
      })}
    </div>
  );
}
